import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import {
  CreditCard,
  FileText,
  ChevronRight,
  Award,
  Building2,
  Monitor,
  Receipt,
  AlertCircle,
} from 'lucide-react';

const penceToPounds = (pence: number) => (pence / 100).toFixed(2);

export default function BillingPage() {
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    isError: subscriptionError,
  } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => apiClient.getSubscription(),
  });

  const {
    data: usage,
    isLoading: usageLoading,
    isError: usageError,
  } = useQuery({
    queryKey: ['billingUsage'],
    queryFn: () => apiClient.getBillingUsage(),
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: () => apiClient.getPaymentMethods(),
  });

  const defaultPayment = paymentMethods?.find((pm) => pm.isDefault);

  if (subscriptionLoading || usageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (subscriptionError || usageError) {
    return (
      <div className="card p-12 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load billing information</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again shortly.</p>
      </div>
    );
  }

  const tierColors: Record<string, string> = {
    BASIC: 'bg-gray-100 text-gray-800',
    PROFESSIONAL: 'bg-primary-100 text-primary-800',
    PREMIUM: 'bg-blue-100 text-blue-800',
    ENTERPRISE: 'bg-purple-100 text-purple-800',
  };

  const tierIcons: Record<string, string> = {
    BASIC: '⭐',
    PROFESSIONAL: '🏆',
    PREMIUM: '💎',
    ENTERPRISE: '👑',
  };

  const tier = subscription?.plan.tier ?? 'BASIC';
  const monthlyAmount = subscription ? penceToPounds(subscription.monthlyAmountPence) : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your subscription, view usage, and update payment methods
        </p>
      </div>

      {/* Current Plan Card */}
      <div className="card">
        <div className="p-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
            Current Plan
          </h2>

          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${tierColors[tier]}`}>
                <span className="text-2xl">{tierIcons[tier]}</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {subscription?.plan.name ?? 'Basic'}
                </h3>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p>Monthly: {monthlyAmount ? `£${monthlyAmount}` : 'N/A'} + VAT</p>
                  <div className="pt-2 border-t border-gray-200 mt-2">
                    <p className="font-medium text-gray-900">
                      {subscription?.restaurantCount ?? 0} restaurant
                      {subscription?.restaurantCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                  <span>
                    Renews:{' '}
                    {subscription?.currentPeriodEnd
                      ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                      : 'N/A'}
                  </span>
                  <span>•</span>
                  <span>
                    Payment: {defaultPayment?.card ? `•••• ${defaultPayment.card.last4}` : 'Not set'}
                  </span>
                </div>
              </div>
            </div>
            <Link to="/billing/plans" className="btn btn-outline h-10">
              Manage
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* Usage Card */}
      <div className="card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Usage This Billing Period
            </h2>
            <span className="text-sm text-gray-500">
              {usage?.periodStart && usage?.periodEnd
                ? `${new Date(usage.periodStart).toLocaleDateString()} – ${new Date(usage.periodEnd).toLocaleDateString()}`
                : ''}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <UsageCard
              icon={Building2}
              label="Restaurants"
              used={usage?.restaurantCount ?? 0}
              included={subscription?.plan.maxRestaurants}
              action={
                <Link to="/restaurants" className="text-xs text-primary-600 hover:text-primary-700">
                  + Add
                </Link>
              }
            />
            <UsageCard icon={Monitor} label="Terminals" used={usage?.terminalCount ?? 0} />
            <UsageCard icon={Receipt} label="Orders" used={usage?.orderCount ?? 0} />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/billing/invoices"
          className="card p-4 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="p-3 bg-gray-100 rounded-lg">
            <FileText className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">View Invoices</h3>
            <p className="text-sm text-gray-500">Download past invoices</p>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
        </Link>

        <Link
          to="/billing/plans"
          className="card p-4 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="p-3 bg-gray-100 rounded-lg">
            <Award className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Change Plan</h3>
            <p className="text-sm text-gray-500">Upgrade or downgrade</p>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
        </Link>

        <Link
          to="/billing/payment-methods"
          className="card p-4 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="p-3 bg-gray-100 rounded-lg">
            <CreditCard className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Update Payment Method</h3>
            <p className="text-sm text-gray-500">Manage cards and direct debit</p>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
        </Link>
      </div>
    </div>
  );
}

interface UsageCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  used: number;
  included?: number;
  unlimited?: boolean;
  percentage?: number;
  subtext?: string;
  action?: React.ReactNode;
}

function UsageCard({
  icon: Icon,
  label,
  used,
  included,
  unlimited,
  percentage,
  subtext,
  action,
}: UsageCardProps) {
  const displayValue = unlimited
    ? `${used}/∞`
    : included
    ? `${used}/${included}`
    : String(used);

  const progress = percentage ?? (included ? (used / included) * 100 : 0);

  return (
    <div className="text-center">
      <div className="flex items-center justify-center mb-2">
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{displayValue}</p>
      {included && !unlimited && (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${
                progress > 90 ? 'bg-red-500' : progress > 70 ? 'bg-yellow-500' : 'bg-primary-500'
              }`}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">{Math.round(progress)}% used</p>
        </div>
      )}
      {subtext && <p className="text-xs text-gray-500 mt-2">{subtext}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
